import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './estilos/global.css';
import { leerReclamaciones } from './nucleo/autorizacion/sesion.js';
import { obtenerSupabase } from './nucleo/supabase/cliente.js';
import { router } from './rutas/arbol.js';

/**
 * La sesión se inyecta en el contexto del router y se refresca en cada cambio de Auth.
 *
 * Se hace acá y no dentro de cada ruta por una razón concreta: `beforeLoad` es
 * síncrono en la práctica y consultar la sesión ahí obligaría a que cada grupo esperara
 * una promesa antes de decidir, lo que produce un parpadeo en cada navegación.
 */
function sincronizarSesion(accessToken: string | null | undefined): void {
  router.update({ context: { sesion: leerReclamaciones(accessToken) } });
  void router.invalidate();
}

const raiz = document.getElementById('raiz');
if (!raiz) throw new Error('Falta el elemento #raiz en index.html');

/**
 * El arranque va dentro de un `try` porque acá es donde se construye el cliente de
 * Supabase, y sin `VITE_SUPABASE_URL` o sin la llave publicable eso lanza.
 *
 * `cliente.ts` construye perezoso justamente para no reventar al importar, pero este
 * archivo lo llama de una y antes de renderizar: sin el `catch`, un despliegue sin las
 * dos variables definidas da una pantalla en blanco y el motivo queda solo en la consola.
 * Con él, quien despliega lee cuál variable falta en la pantalla misma.
 */
try {
  const { data: inicial } = await obtenerSupabase().auth.getSession();
  sincronizarSesion(inicial.session?.access_token);

  obtenerSupabase().auth.onAuthStateChange((_evento, sesion) => {
    sincronizarSesion(sesion?.access_token);
  });

  createRoot(raiz).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
} catch (error) {
  const motivo = error instanceof Error ? error.message : String(error);
  raiz.textContent = `La aplicación no pudo arrancar: ${motivo}`;
  throw error;
}
